import {action, makeObservable, observable, runInAction, computed} from 'mobx';
import Get from 'app/utils/Get';
import Delete from 'app/utils/Delete';
import BonusInterface from 'app/interfaces/BonusInterface';
import BonusDataInterface from 'app/interfaces/BonusDataInterface';
import {UserStore} from 'app/store/UserStore';
import Post from 'app/utils/Post';
import Patch from 'app/utils/Patch';
import {toastError, toastSuccess} from 'app/components/toasts/liteToast';
import I18n from 'app/i18n';
import Websocket from 'modules/Websocket';
import FreespinInterface from 'app/interfaces/Freespinsinterface';
import NotificationStore from 'app/store/notifications';
import PopupFreespins from 'app/components/my_profile/freespins/PopupFreespins';
import {showPopup} from 'app/utils/modals/ModalsService';
import React from 'react';
import ShopItemInterface from 'app/interfaces/shop/ShopItemInterface';
import {CURRENCY_USD, POINTS} from 'app/utils/ShopItemPriceFormat';
import PopupBonuses from 'app/components/my_profile/bonuses/PopupBonuses';
import PopupPromoMoney from 'app/components/my_profile/bonuses/PopupPromoMoney';
import PromoMoneyInterface from 'app/interfaces/PromoMoneyInterface';
import {ELEMENT_NOT_FOUND} from 'app/utils/Consts';
import {modalsStore, shopLootboxStore} from 'app/store/global';
import {pushAnalyticsEvent} from 'app/utils/analytics';

const POPUP_DELAY = 10000;
const POPUP_FORCE_DELAY = 1000;

export interface UpdateBonusesType {
    bonuses?: BonusInterface[],
    bonuses_data?: BonusDataInterface,
    bonusesLoading?: boolean
}

export class UserBonusesStore {
    @observable bonuses: BonusInterface[] = [];

    @observable bonuses_data: BonusDataInterface = null;

    @observable bonusesLoading = false;

    @observable bonusDataLoading = false;

    @observable free_spins: FreespinInterface[] = [];

    @observable bonusesLootboxes: ShopItemInterface[] = [];

    userStore: UserStore;

    subscriptions = [];

    constructor() {
        makeObservable(this);
    }

    @computed
    get availableBonuses(): BonusInterface[] {
        return this.bonuses.filter(bonus => !bonus.active).sort((a, b) => a.time_left - b.time_left);
    }

    @computed
    get activeBonus(): BonusInterface | null {
        return this.bonuses.find(bonus => bonus.active) || null;
    }

    @computed
    get sortedFreeSpins(): FreespinInterface[] {
        return this.free_spins.sort((a, b) => a.time_left - b.time_left);
    }

    @action
    setUserStore = (store: UserStore): void => {
        this.userStore = store;
    };

    @action
    setFreeSpins = (data: FreespinInterface[]): void => {
        this.free_spins = data;
    };

    @action
    cancelFreeSpin(id: string): void {
        new Delete({url: `/casino/free_spins/${id}`})
            .execute()
            .then(response => response.json())
            .then(response => {
                if (response.status !== 'ok') {
                    toastError(response.message || I18n.t('casino_free_spins.canceled_error'));
                }
            })
            .catch(error => {
                console.error('Error canceling free spin:', error);
                toastError(I18n.t('casino_free_spins.canceled_error'));
            });
    }

    @action
    freeSpinCancelledNotice(payload: {id: string; message: string}): void {
        this.removeFreeSpinAfterCancel(payload.id);
        toastSuccess(payload.message || I18n.t('casino_free_spins.canceled_success'));
    }

    @action
    freeSpinCancelErrorNotice(payload: {id: string; message: string}): void {
        toastError(payload.message || I18n.t('casino_free_spins.canceled_error'));
    }

    @action
    removeFreeSpinAfterCancel(id: string): void {
        const spinIndex = this.free_spins.findIndex(spin => spin.id === id);

        if (spinIndex !== ELEMENT_NOT_FOUND) {
            this.free_spins.splice(spinIndex, 1);
        }
    }

    @action
    updateUserBonusData({
        bonuses, bonuses_data
    }: UpdateBonusesType): void {
        this.bonuses = bonuses || [];
        this.bonuses_data = bonuses_data;
    }

    @action
    updateBonusesLoading(loading = false): void {
        this.bonusesLoading = loading;
    }

    @action
    updateBonusDataLoading(loading = false): void {
        this.bonusDataLoading = loading;
    }

    @action
    updateBonusesLootboxes(data: ShopItemInterface[]): void {
        this.bonusesLootboxes = data;
    }

    @action
    getUserBonusesData(loading = true): void {
        this.updateBonusDataLoading(loading);

        new Get({url: '/my_profile/bonuses_data'})
            .execute()
            .then(data => data.json())
            .then(data => {
                this.updateBonusDataLoading();
                this.updateStore({...data});
                this.userStore.updateBonuses(data.bonuses);
            });
    }

    @action
    updateStore({bonuses, bonuses_data}:
            { bonuses?: BonusInterface[], bonuses_data?: BonusDataInterface, loading: boolean}): void {
        if (bonuses) {
            this.bonuses = bonuses;
        }
        if (bonuses_data) {
            this.bonuses_data = bonuses_data;
        }
    }

    @action
    removeBonusAfterCancel = (id: number): void => {
        const objWithIdIndex = this.bonuses.findIndex(obj => obj.id === id);
        const notFoundIndex = -1;
        const recordFound = objWithIdIndex > notFoundIndex;

        if (recordFound) {
            this.bonuses.splice(objWithIdIndex, 1);
        }
    };

    cancelBonus = (id: number): void => {
        new Post({
            params: {bonus_id: id},
            url: '/user/cancel_bonus'
        }).execute()
            .then((response: Response) => response.json())
            .then(response => {
                if (response.success) {
                    toastSuccess(I18n.t('cancelled_bonus_message', {id}));
                    this.removeBonusAfterCancel(id);
                } else {
                    toastError(response.message);
                }
            });
    };

    activateBonus = (id: number): void => {
        new Post({
            params: {bonus_id: id},
            url: '/user/activate_bonus'
        }).execute()
            .then((response: Response) => response.json())
            .then(response => {
                if (response.success) {
                    pushAnalyticsEvent('bonus_activate', {
                        bonus_id: id,
                        source: 'available_bonus'
                    });
                    toastSuccess(I18n.t('activated_bonus_message', {id}));
                    this.getUserBonusesData(false);
                } else {
                    toastError(response.message);
                }
            });
    };

    markBonusViewed = (id: number): void => {
        new Patch({
            params: {bonus_id: id},
            url: '/my_profile/mark_bonus_viewed'
        }).execute()
            .then((response: Response) => response.json())
            .then(response => {
                if (response?.success) {
                    runInAction(() => {
                        const bonus = this.bonuses.find(item => item.id === id);

                        if (bonus) {
                            bonus.viewed = true;
                        }

                        if (this.userStore?.user?.bonuses?.length) {
                            this.userStore.updateBonuses(
                                this.userStore.user.bonuses.map(userBonus => userBonus.id === id
                                    ? {...userBonus, viewed: true}
                                    : userBonus)
                            );
                        }
                    });
                }
            })
            .catch(error => console.error('Error marking bonus viewed:', error));
    };

    markBonusPopupViewed = (id: number): void => {
        new Patch({
            params: {bonus_id: id},
            url: '/my_profile/mark_bonus_popup_viewed'
        }).execute()
            .then((response: Response) => response.json())
            .then(response => {
                if (response?.success) {
                    runInAction(() => {
                        const bonus = this.bonuses.find(item => item.id === id);

                        if (bonus) {
                            bonus.popup_viewed = true;
                        }
                    });
                }
            })
            .catch(error => console.error('Error marking bonus popup viewed:', error));
    };

    listen(): void {
        this.getUserBonusesData(false);
    }

    showNotViewedBonusPopup = (force = false): void => {
        const timeout = force ? POPUP_FORCE_DELAY : POPUP_DELAY;

        setTimeout(() => {
            const notViewedBonus = this.bonuses.find(bonus => !bonus.viewed && !bonus.popup_viewed);

            if (notViewedBonus) {
                this.showBonusPopup(notViewedBonus);
            }
        }, timeout);
    };

    showBonusPopup(bonus: BonusInterface): void {
        const popupId = 'popup-bonuses';
        const {lootboxOpeningInProgress} = shopLootboxStore;
        const showBonusesPopup = !lootboxOpeningInProgress && !modalsStore.isModalOpen(popupId);

        if (showBonusesPopup) {
            pushAnalyticsEvent('bonus_view', {
                bonus_id: bonus.id,
                bonus_type: bonus.type,
                source: 'bonus_popup'
            });
            showPopup(popupId, () => <PopupBonuses
                popupId={popupId}
                bonus={bonus}
                hasActiveBonus={Boolean(this.activeBonus)}
                activateBonus={this.activateBonus}
                showNotViewedBonusPopup={this.showNotViewedBonusPopup}
            />);
            this.markBonusPopupViewed(bonus.id);
        }
    }

    @action
    bonus_notice(payload: BonusInterface): void {
        const existingBonusIndex = this.bonuses.findIndex(bonus => bonus.id === payload.id);

        if (existingBonusIndex === ELEMENT_NOT_FOUND) {
            this.bonuses.push(payload);
        }

        this.showBonusPopup(payload);
    }

    @action
    promo_money_notice(payload: PromoMoneyInterface): void {
        const popupId = 'popup-bonuses';
        const {lootboxOpeningInProgress} = shopLootboxStore;
        const canShowPopup = !lootboxOpeningInProgress && !modalsStore.isModalOpen(popupId);

        if (canShowPopup) {
            showPopup(popupId, () => <PopupPromoMoney popupId={popupId} promoMoney={payload} />);
            return;
        }

        NotificationStore.add({
            data: {amount: payload.amount},
            force: true,
            key: `promo-money-notification-${Date.now()}`,
            timeout: true,
            type: 'money_received'
        });
    }

    @action
    free_spin_notice(payload: FreespinInterface): void {
        const existingSpinIndex = this.free_spins.findIndex(spin => spin.id === payload.id);

        if (existingSpinIndex === ELEMENT_NOT_FOUND) {
            this.free_spins.push(payload);
        }

        if (window.location.pathname.includes('/casino')) {
            showPopup('popup-freespins', () => <PopupFreespins popupId={'popup-freespins'} free_spin_data={payload} />);
        } else {
            NotificationStore.add({
                force: true,
                key: `free_spin_notification-${payload.id}`,
                payload,
                timeout: false,
                type: 'free_spin_notification'
            });
        }
    }

    @action
    initBonusesLootboxes = (): void => {
        new Get({url: '/shop/bonus_lootboxes'})
            .execute()
            .then(data => data.json())
            .then(data => {
                const bonus_lootboxes = [];
                data.forEach(item => {
                    if (Boolean(Number(item.cost_money)) && Boolean(Number(item.cost_points))) {
                        bonus_lootboxes.push({...item,
                            currency: CURRENCY_USD});
                    }
                    item.currency = POINTS;
                    bonus_lootboxes.push(item);
                });
                this.updateBonusesLootboxes(bonus_lootboxes);
            });
    };

    unsubscribe(userId: number): void {
        let subscription = this.subscriptions[`${userId}`];
        if (subscription) {
            Websocket.unsubscribe(subscription);
            subscription = null;
        }
    }

    freeSpinsCount = (): number => this.free_spins.length;

    hasNotViewedBonuses = (): boolean => this.bonuses.some(bonus => !bonus.viewed);

    hasActiveViewedBonuses = (): boolean => this.bonuses.some(bonus => bonus.active && bonus.viewed === true);
}
